// ForgotPasswordPage.js — Solicita email para enviar link de recuperação
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Loader2, Mail, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // Em modo dev (sem SendGrid integrado), o backend devolve o link diretamente
      if (r.data?.dev_link) setDevLink(r.data.dev_link);
      toast.success("Se o email existir, foi enviado um link de recuperação.");
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao processar pedido";
      toast.error(typeof message === "string" ? message : "Erro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-sm text-slate-500 hover:text-brand-600 mb-4" data-testid="back-login">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar ao login
        </Link>
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-brand-600">Recuperar Password</CardTitle>
            <CardDescription>
              Indique o seu email. Se a conta existir, enviaremos um link para definir
              uma nova password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!sent ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="o.seu@email.pt"
                    className="mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A enviar...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Enviar link
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-3" data-testid="forgot-sent-state">
                <div className="text-sm text-slate-700 bg-brand-50 border border-brand-200 rounded p-3">
                  ✅ Se a conta existir, foi enviado um link válido por 1 hora.
                  Verifique a sua caixa de correio.
                </div>
                {devLink && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 space-y-1.5" data-testid="dev-link-box">
                    <div className="font-semibold">[Modo DEV] Envio de email MOCKADO</div>
                    <div>O link de reset abaixo é exibido para testes. Quando integrar SendGrid, este bloco desaparece.</div>
                    <div className="flex items-center gap-1 mt-1">
                      <Input value={devLink} readOnly className="text-[10px] font-mono h-7" />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => {
                          navigator.clipboard.writeText(devLink);
                          toast.success("Link copiado");
                        }}
                        data-testid="copy-dev-link"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    Voltar ao Login
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
